import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Loader2, CheckCircle2, ArrowRight, X, ScrollText } from 'lucide-react';
import { toast } from 'sonner';

const API = window.location.origin;

export default function FarmSignupPage() {
  const [step, setStep] = useState('form'); // form | checkout | complete
  const [loading, setLoading] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const [form, setForm] = useState({
    company_name: '', admin_email: '', admin_first_name: '', admin_last_name: '',
    billing_cycle: 'monthly', setup_assistance: false,
  });
  const [tosOpen, setTosOpen] = useState(false);
  const [tosContent, setTosContent] = useState('');

  useEffect(() => {
    fetch(`${API}/api/system-settings/terms`)
      .then(r => r.json())
      .then(d => setTosContent(d.content || ''))
      .catch(() => {});
  }, []);


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.company_name.trim() || !form.admin_email.trim() || !form.admin_first_name.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/billing/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, is_farm: true }),
      });

      let checkoutUrl = null;

      if (r.ok) {
        try {
          const data = await r.json();
          checkoutUrl = data.checkout_url;
        } catch (bodyErr) {
          if (bodyErr.message && bodyErr.message !== 'body is disturbed or locked'
              && !bodyErr.message.includes('JSON')) {
            throw bodyErr;
          }
          // Body unreadable but server returned OK — build URL from farm product permalink
          checkoutUrl = `https://barndoorsoftware.gumroad.com/l/grgvjze?email=${encodeURIComponent(form.admin_email)}`;
        }
      } else {
        let detail = 'Signup failed';
        try { const d = await r.json(); detail = d.detail || detail; } catch { /* ignore */ }
        throw new Error(detail);
      }

      const url = checkoutUrl + (checkoutUrl.includes('?') ? '&' : '?') + 'wanted=true';
      setCheckoutUrl(url);
      setStep('checkout');
    } catch (e) {
      toast.error(e.message || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'complete') return (
    <div className="min-h-screen bg-secondary flex flex-col items-center justify-center p-4">
      <Card className="max-w-md w-full shadow-lg">
        <CardContent className="pt-10 pb-10 text-center">
          <CheckCircle2 className="w-16 h-16 text-primary mx-auto mb-4" />
          <h2 className="font-serif text-xl font-bold mb-4">Setup Complete</h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {form.setup_assistance
              ? 'You have requested Set Up Assistance. A member of the Barn Door Software team will reach out to you shortly.'
              : 'Please check your email for your login details.'}
          </p>
          <p className="text-sm text-muted-foreground mt-3">Thank you!</p>
        </CardContent>
      </Card>
    </div>
  );

  if (step === 'checkout') return (
    <div className="min-h-screen bg-secondary flex flex-col items-center justify-center p-4">
      <Card className="w-full max-w-xl shadow-lg">
        <CardContent className="p-0">
          <div className="flex items-center justify-between px-4 pt-4 pb-2">
            <h2 className="font-serif text-lg font-bold">Complete Payment</h2>
            <button onClick={() => setStep('form')} className="text-muted-foreground hover:text-foreground">
              <X className="w-5 h-5" />
            </button>
          </div>
          {checkoutUrl && (
            <iframe
              src={checkoutUrl}
              className="w-full border-0"
              style={{ height: '70vh' }}
              title="Checkout"
              allow="payment"
            />
          )}
          <div className="px-4 pb-4 pt-2 text-center border-t">
            <p className="text-xs text-muted-foreground mb-2">Once your payment is complete:</p>
            <Button onClick={() => setStep('complete')} className="bg-primary hover:bg-primary/90 text-white w-full">
              Get my Login Details
            </Button>
            <p className="text-xs text-muted-foreground mt-2">
              Only click after Gumroad confirms your payment and you see a receipt or confirmation screen.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-secondary flex flex-col items-center justify-center p-4">
      {/* Logo and Tagline — same as login page */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center gap-3 mb-3">
          <img src="/logo.svg" alt="Green Door" className="w-32 h-32 object-contain" />
        </div>
        <p className="text-muted-foreground text-sm tracking-wide">
          Reining in your data to power your Stable and Sales
        </p>
      </div>

      <Card className="w-full max-w-md shadow-lg">
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1">
              <Label>Company Name *</Label>
              <Input value={form.company_name} onChange={e => setForm({ ...form, company_name: e.target.value })} placeholder="e.g. Bluegrass Farm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>First Name *</Label>
                <Input value={form.admin_first_name} onChange={e => setForm({ ...form, admin_first_name: e.target.value })} placeholder="First name" />
              </div>
              <div className="space-y-1">
                <Label>Last Name</Label>
                <Input value={form.admin_last_name} onChange={e => setForm({ ...form, admin_last_name: e.target.value })} placeholder="Last name" />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Email Address *</Label>
              <Input type="email" value={form.admin_email} onChange={e => setForm({ ...form, admin_email: e.target.value })} placeholder="admin@yourfarm.com" />
            </div>

            <div className="flex items-start gap-2 border rounded-md p-3 bg-muted/10">
              <input type="checkbox" id="setup-assist-farm" checked={form.setup_assistance}
                onChange={e => setForm({ ...form, setup_assistance: e.target.checked })}
                className="mt-0.5 rounded" />
              <label htmlFor="setup-assist-farm" className="cursor-pointer">
                <p className="text-sm font-medium">Set Up Assistance</p>
                <p className="text-xs text-muted-foreground">Our team will help configure your account, import data, and get you started for a flat fixed fee.</p>
              </label>
            </div>

            <Button type="submit" disabled={loading}
              className="w-full h-11 text-base bg-primary hover:bg-primary/90 text-white">
              {loading ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <ArrowRight className="w-5 h-5 mr-2" />}
              Continue to Payment
            </Button>

            <p className="text-xs text-center text-muted-foreground -mt-1">
              By continuing to payment you accept our{' '}
              <button
                type="button"
                onClick={() => setTosOpen(true)}
                className="text-primary hover:underline font-medium"
              >
                Terms of Service
              </button>
            </p>

            <p className="text-xs text-center text-muted-foreground">
              Already have an account? <a href="/login" className="text-primary hover:underline">Sign in</a>
            </p>
          </form>
        </CardContent>
      </Card>

      {/* Terms of Service Modal */}
      <Dialog open={tosOpen} onOpenChange={setTosOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ScrollText className="w-5 h-5" /> Terms of Service
            </DialogTitle>
          </DialogHeader>
          <div
            className="overflow-y-auto flex-1 prose prose-sm max-w-none pr-2
              [&_h2]:text-lg [&_h2]:font-bold [&_h2]:mt-4 [&_h2]:mb-2
              [&_h3]:text-base [&_h3]:font-semibold [&_h3]:mt-4 [&_h3]:mb-1
              [&_p]:mb-3 [&_p]:text-sm [&_p]:leading-relaxed
              [&_strong]:font-semibold [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1"
            dangerouslySetInnerHTML={{ __html: tosContent }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
